import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../axiosSetup";
import CustomNavbar from '../CustomNavbar';
import cfg from '../../config';

const ZRPersonnelFileCreate = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const isEditMode = !!state?.entry;
  const memberID = state?.memberID ?? null;

  const [formData, setFormData]       = useState({});
  const [error, setError]             = useState(null);
  const [loading, setLoading]         = useState(false);
  const [entryTypes, setEntryTypes]   = useState([]);
  const [selectedType, setSelectedType] = useState("");

  // ── Typen laden ─────────────────────────────────────────────
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const res = await api.get("/api/all-personnel-entries-types-zunftrat");
        setEntryTypes(res.data.map(t => ({
          ...t,
          formular_scheme: JSON.parse(t.formular_scheme),
        })));
      } catch (err) {
        setError("Fehler beim Laden der Eintragstypen: " + err.message);
      }
    };
    fetchTypes();
  }, []);

  // ── Edit-Modus: Typ vorbelegen ───────────────────────────────
  useEffect(() => {
    if (state?.entry?.typid) setSelectedType(state.entry.typid.toString());
  }, [state]);

  // ── Edit-Modus: Felder aus gespeichertem JSON befüllen ───────
  useEffect(() => {
    if (!state?.entry || entryTypes.length === 0) return;
    const { typid, data } = state.entry;
    const type = entryTypes.find(t => t.ID === parseInt(typid));
    if (!type) return;
    try {
      const parsed = JSON.parse(data);
      const mapped = {};
      for (const field of type.formular_scheme) {
        const key = Object.keys(parsed).find(
          k => parsed[k]["frontend-name"] === field["frontend-name"] || k === field.name
        );
        mapped[field.name] = key ? parsed[key].value : "";
      }
      setFormData(mapped);
    } catch (err) {
      console.error("Fehler beim Parsen der Eintragsdaten:", err);
    }
  }, [entryTypes, state]);

  // ── Typ wechseln ─────────────────────────────────────────────
  const handleTypeChange = (e) => {
    const id = e.target.value;
    setSelectedType(id);
    const type = entryTypes.find(t => t.ID === parseInt(id));
    if (type) {
      setFormData(type.formular_scheme.reduce((acc, f) => {
        acc[f.name] = formData[f.name] || "";
        return acc;
      }, {}));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ── Daten für API transformieren ─────────────────────────────
  const transformFormData = (data, typeID) => {
    const type = entryTypes.find(t => t.ID === parseInt(typeID));
    if (!type) return {};
    return Object.entries(data).reduce((acc, [key, value]) => {
      const field = type.formular_scheme.find(f => f.name === key);
      if (field) acc[key] = { value: value || "", "frontend-name": field["frontend-name"], type: field.type || "text" };
      return acc;
    }, {});
  };

  // ── Absenden ─────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const payload = {
        memberID,
        type: selectedType,
        data: transformFormData(formData, selectedType),
      };
      if (isEditMode) {
        payload.ID = state.entry.ID;
        await api.post("/api/edit-personnel-entry-zunftrat", payload);
        navigate(-1, { state: { message: "Eintrag erfolgreich bearbeitet!" } });
      } else {
        await api.post("/api/create-personnel-entry-zunftrat", payload);
        navigate(-1, { state: { message: "Eintrag erfolgreich erstellt!" } });
      }
    } catch (err) {
      setError("Fehler beim Speichern: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const currentType = entryTypes.find(t => t.ID === parseInt(selectedType));

  return (
    <div>
      <CustomNavbar />
      <div className="container mt-4" style={{ maxWidth: 680 }}>
        <h4 className="mb-4">{isEditMode ? "Eintrag bearbeiten" : "Neuen Eintrag erstellen"}</h4>

        {error && <div className="alert alert-danger">{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Typ-Auswahl */}
          <div className="mb-4">
            <label className="form-label fw-semibold">Typ</label>
            <select
              className="form-select"
              value={selectedType}
              onChange={handleTypeChange}
              required
              disabled={isEditMode}
            >
              <option value="">Bitte wählen…</option>
              {entryTypes.map(t => (
                <option key={t.ID} value={t.ID}>{t.name}</option>
              ))}
            </select>
            {currentType?.description && (
              <div className="form-text text-muted mt-1">{currentType.description}</div>
            )}
          </div>

          {/* Dynamische Felder */}
          {currentType?.formular_scheme.map(field => (
            <div key={field.name} className="mb-3">
              <label htmlFor={field.name} className="form-label fw-semibold">
                {field["frontend-name"]}
                {field.required === "true" && <span className="text-danger ms-1">*</span>}
              </label>

              {field.type === "text" && (
                <input
                  type="text"
                  className="form-control"
                  id={field.name}
                  name={field.name}
                  value={formData[field.name] || ""}
                  onChange={handleInputChange}
                  required={field.required === "true"}
                />
              )}

              {field.type === "textarea" && (
                <textarea
                  className="form-control"
                  id={field.name}
                  name={field.name}
                  rows={3}
                  value={formData[field.name] || ""}
                  onChange={handleInputChange}
                  required={field.required === "true"}
                />
              )}

              {field.type === "date" && (
                <input
                  type="date"
                  className="form-control"
                  id={field.name}
                  name={field.name}
                  value={formData[field.name] || ""}
                  onChange={handleInputChange}
                  required={field.required === "true"}
                />
              )}

              {field.type === "select" && (
                <select
                  className="form-select"
                  id={field.name}
                  name={field.name}
                  value={formData[field.name] || ""}
                  onChange={handleInputChange}
                  required={field.required === "true"}
                >
                  <option value="">Bitte wählen…</option>
                  {(field.options || []).map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              )}

              {field.name === "gesperrtBis" && (
                <div className="form-text text-warning mt-1">
                  ⚠️ Dieses Datum wird automatisch als Sperrdatum gesetzt.
                </div>
              )}
            </div>
          ))}

          {selectedType && (
            <div className="d-flex gap-2 mt-4">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Wird gespeichert…" : isEditMode ? "Änderungen speichern" : "Eintrag erstellen"}
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
                Abbrechen
              </button>
            </div>
          )}
        </form>
        <br />
      </div>
    </div>
  );
};

export default ZRPersonnelFileCreate;
